import _ from 'lodash';
import { ComponentProps, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { ConversationIcon } from '@treams/treams-icons';
import { Empty, Skeleton } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import Comment from './components/Comment';
import CommentComposer from './components/CommentComposer';

const types = {
	EvaluationSubmission: graphql.CommentTargetType.EvaluationSubmission,
	FeedbackResponse: graphql.CommentTargetType.FeedbackResponse,
	Goal: graphql.CommentTargetType.Goal,
	Note: graphql.CommentTargetType.Note,
};

function CommentsSkeleton() {
	return (
		<Skeleton.Repeat count={3}>
			<Box mb={2}>
				<Comment.Skeleton />
			</Box>
		</Skeleton.Repeat>
	);
}

type CommentViewWithMetadata = ComponentProps<typeof Comment>['comment'] &
	Pick<graphql.Comment, 'metadata'>;

export function CommentsWithData(
	props: CommentsProps & {
		comments: CommentViewWithMetadata[];
		onRefetch: () => void;
	},
) {
	const { t } = useTranslation();
	const [comments, setComments] = useState(props.comments);

	// Refresh once a comment is created.
	const commentIds = props.comments.map(({ id }) => id).join(',');
	useEffect(() => setComments(props.comments), [commentIds, props.comments]);

	// Refresh once a comment is archived.
	const isArchived = props.comments.filter(({ isArchived }) => isArchived === true);
	useEffect(() => setComments(props.comments), [isArchived, props.comments]);

	const filteredComments = comments.filter((comment) =>
		_.isEqual(
			_.fromPairs(comment.metadata?.map(({ key, value }) => [key, value])),
			props.metadata ?? {},
		),
	);

	const composerElement = (
		<CommentComposer
			targetID={props.target.id}
			targetType={types[props.target.__typename!]}
			metadata={props.metadata}
			autoFocus={props.autoFocus}
			onCancel={props.onCancelComposer}
			onCreate={(comment: CommentViewWithMetadata) => {
				setComments([comment].concat(comments ?? []));
				props.onCancelComposer?.();
			}}
			onRefresh={props.onRefetch}
		/>
	);

	const orderedComments =
		props.alignComposer === 'top' ? filteredComments : filteredComments.slice().reverse();

	return (
		<>
			{props.target.viewerCanComment &&
				props.composer !== false &&
				props.alignComposer === 'top' && <Box mb={4}>{composerElement}</Box>}

			{filteredComments.length === 0 ? (
				props.empty === false ? null : (
					<Skeleton.Empty
						contents={<Empty icon={<ConversationIcon />} title={t('comments.empty.title')} />}
					>
						<Skeleton.Repeat count={3}>
							<Box mb={2}>
								<Comment.Skeleton />
							</Box>
						</Skeleton.Repeat>
					</Skeleton.Empty>
				)
			) : (
				orderedComments.map((comment) => (
					<Box key={comment.id} mb={3}>
						<Comment comment={comment} onRefresh={props.onRefetch} />
					</Box>
				))
			)}

			{props.target.viewerCanComment &&
				props.composer !== false &&
				(props.alignComposer === undefined || props.alignComposer === 'bottom') && (
					<Box mt={4}>{composerElement}</Box>
				)}
		</>
	);
}

type CommentTargetView = Pick<graphql.CommentTarget, 'viewerCanComment'> & {
	__typename?: keyof typeof types;
	id: string;
};

export type CommentsProps = {
	target: CommentTargetView;
	metadata?: Record<string, string>;
	empty?: boolean;
	composer?: boolean;
	alignComposer?: 'top' | 'bottom';
	autoFocus?: boolean;
	onCancelComposer?: () => void;
};

export default function Comments(props: CommentsProps) {
	const target = {
		targetType: types[props.target.__typename!],
		targetID: props.target.id,
	};

	const query = graphql.useCommentsQuery({
		variables: { ...target },
	});

	return (
		<Box>
			{query.data ? (
				<CommentsWithData
					target={props.target}
					metadata={props.metadata}
					comments={query.data.comments}
					empty={props.empty}
					composer={props.composer}
					alignComposer={props.alignComposer}
					autoFocus={props.autoFocus}
					onCancelComposer={props.onCancelComposer}
					onRefetch={() => query.refetch()}
				/>
			) : (
				<CommentsSkeleton />
			)}
		</Box>
	);
}

Comments.Skeleton = CommentsSkeleton;
